import { ApplicationContext } from '@/store/modules/application-context/types/application-context.types';
import { NavigationGuard } from 'vue-router';
import { BaseCourse } from './jbi-shared/dto/jtms-public-course.dtos';
import {
  redirectToLoginSignup,
  redirectToPageNotFound
} from './store/plugins/forbidden-response-handler.plugin';
import store, { RootState } from './store/store';

const groupRegistrationEnabled = (): boolean =>
  (store.state as RootState).course.courseBySlug?.groupRegistration === true;

const courseFromSlug = (): BaseCourse | undefined =>
  (store.state as RootState).course.courseBySlug;

const isNotLoggedIn = (): boolean =>
  !(store.state as RootState).auth.isLoggedIn;

/**
 * Performs the standard authentication check for a logged-in user.
 * Verifies the session health and handles redirection if the user is not logged in.
 * @param redirectionPath (Optional) The path to redirect to after a successful login
 * @param next The navigation guard's next function
 * @returns A promise that resolves to `true` if authentication passes, and `false` if it fails.
 */
async function performStandardAuthCheck(
  redirectionPath: string,
  next: (arg?: any) => void
): Promise<boolean> {
  try {
    await store.dispatch('auth/checkSessionHealth');

    // Re-check state after the API call
    if (isNotLoggedIn()) {
      redirectToLoginSignup(redirectionPath);
      next(false);
      // Signal that the check failed
      return false;
    }

    // Signal that the check passed
    return true;
  } catch (error) {
    console.error('An error occurred during standard auth check:', error);
    redirectToLoginSignup(redirectionPath);
    next(false);
    return false;
  }
}

/**
 * Validates a course by its slug. Fetches the course and handles redirects for invalid slugs.
 * @param slug The course slug from the route parameters.
 * @param next The navigation guard's next function.
 * @returns A promise that resolves with the course object on success, or null on failure.
 */
async function validateAndFetchCourse(
  slug: string,
  next: (arg?: any) => void
): Promise<BaseCourse | null> {
  if (!slug) {
    console.error('Missing slug from route params.');
    redirectToPageNotFound();
    next(false);
    return null;
  }

  try {
    await store.dispatch('course/getCourseBySlug', slug);

    if (!courseFromSlug()) {
      redirectToPageNotFound();
      next(false);
      return null;
    }

    return courseFromSlug()!;
  } catch (error) {
    // Catches any unexpected errors from the API
    console.error(`Course validation failed for slug "${slug}":`, error);
    redirectToPageNotFound();
    next(false);
    return null;
  }
}

/**
 * **Responsibility: Authentication**
 *
 * It answers the question:
 * "Is this user's session valid enough to proceed?"
 *
 * This guard lets public/course details/conditional auth routes pass, then for all
 * other routes, it ensures a healthy session exists by calling `checkSessionHealth`.
 * If the session is invalid, it correctly redirects to login.
 */
export const GeneralRouteGuard: NavigationGuard = async (to, from, next) => {
  try {
    // Fully public routes
    if (to.meta && to.meta.isPublic) {
      return next();
    }

    // 1. Custom validation for any course details page navigation
    // Course details pages are only accessible if the course is:
    // - `visibility` = PUBLIC
    // - `status` = PUBLISHED or COMPLETED
    if (to.meta && to.meta.isCourseDetailsPage) {
      // Even for course details page, a check is still required
      const course = await validateAndFetchCourse(to.params.slug, next);

      if (!course) {
        // Validation failed, helper handled redirect.
        return;
      }

      return next();
    }

    // 2. Handle conditionally public checkout routes
    // Course checkout pages are only accessible if the course is:
    // - `visibility` = PUBLIC
    // - `status` = PUBLISHED
    if (to.meta && to.meta.requiresConditionalAuth) {
      const course = await validateAndFetchCourse(to.params.slug, next);

      if (!course) {
        // Validation failed, helper handled redirect, terminate guard
        return;
      }

      // Additional check for course status, COMPLETED courses should not have checkout
      if (
        store.getters['course/isCourseCompleted'] ||
        store.getters['course/isCourseInProgress']
      ) {
        redirectToPageNotFound();
        return next(false);
      }

      // Extend with other conditions in the future, for now, it's just group registration
      if (store.getters['course/groupRegistrationEnabled']) {
        // Run standard login check
        const isAuthenticated = await performStandardAuthCheck(
          to.fullPath,
          next
        );

        // If the check failed, helper method already handled the redirect,
        // so just terminate guard
        if (!isAuthenticated) {
          return;
        }
      }
      // If group registration is not enabled, the route is public.
      // If the user was authenticated, the check passed. In either case, user can proceed.
      return next();
    }

    // 3. Handle routes that require explicit authentication
    if (to.meta && to.meta.authRequired) {
      const isAuthenticated = await performStandardAuthCheck(to.fullPath, next);

      // If the check failed, stop. Otherwise, the user is authenticated and can proceed
      if (!isAuthenticated) {
        return;
      }
    }

    // Default final redirection, assumes that previous checks passed
    return next();
  } catch (error) {
    // This catch block handles unexpected errors during the checks.
    // The forbiddenResponseHandlerPlugin will handle 401s, but for other errors,
    // it's safest to send the user to the login page.
    console.error('An error occurred during GeneralRouteGuard checks:', error);
    redirectToLoginSignup();
    return next(false);
  }
};

/**
 * This guard has three possible states to look for:
 * - If the user is an admin (has the role super-admin-role), in which
 *   case the user has unrestricted access
 * - If the user is accessing a page that is not a module like the
 *   home page or the about page
 * - If the user is accessing a page marked as a module as a non-admin,
 *   in which case the system checks if the module is included in the
 *   roles that they are in. The resolve of the guard in this case will
 *   depend on whether the user has access to the entire module, or if
 *   they have access through an instance (access to specific groups,
 *   for example) or if they have partial access to a module (able to
 *   view products but not create, etc.).
 * @param to
 * @param from
 * @param next
 * @constructor
 */
export const CasbinRouteGuard: NavigationGuard = async (to, from, next) => {
  if (to.meta?.isPublic || to.meta?.authRequired === false) {
    return next();
  }

  if (
    to.meta &&
    to.meta.requiresConditionalAuth &&
    !groupRegistrationEnabled()
  ) {
    return next();
  }

  const userPermissionsMatrix = store.getters['rbac/userPermissionsMatrix'];
  /**
   * Avoid calling /user-permissions-matrix API unless page is reloaded or
   * the matrix is not there in the store.
   *
   * !from = page has been reloaded and the user has not navigated to a page
   * **from** somewhere.
   */
  if ((!userPermissionsMatrix || !from) && !to.meta?.isPublic) {
    await store.dispatch('rbac/getUserPermissionsMatrix');
  }

  const rootModules = store.getters['rbac/rootModules'] ?? [];
  const readModules = store.getters['rbac/readModules'] ?? [];
  const isAdmin = store.getters['rbac/isAdmin'] ?? false;
  const module = to.meta?.module;
  const instanceType = to.meta?.instanceType;

  if (isAdmin) {
    return next();
  } else if (module === 'non_module') {
    return next();
  } else if (rootModules.length > 0 && rootModules.includes(module)) {
    return next();
  } else if (
    readModules.length > 0 &&
    checkForImplicitAccess(module, readModules)
  ) {
    return next();
  } else if (instanceType) {
    if (moduleHasInstances(module, instanceType, readModules)) {
      return next();
    }
  } else {
    return next({ name: 'access-forbidden' });
  }

  return next({ name: 'access-forbidden' });
};

function checkForImplicitAccess(
  targetModule: string,
  readModules: string[]
): boolean {
  for (const module of readModules) {
    if (hasImplicitMatch(module, targetModule)) {
      return true;
    }
  }

  return false;
}

function hasImplicitMatch(module: string, targetModule: string): boolean {
  return module.startsWith(targetModule);
}

function moduleHasInstances(
  module: string,
  instanceType: string,
  readModules: string[]
): boolean {
  const isJbicModule = module.startsWith('jbic_management');
  const subdivisionPattern = isJbicModule
    ? `subdivision_\\w+_${instanceType}s-(images|events)-(read|view)`
    : `subdivision_\\w+_${instanceType}s(?:-(images|events))?-(read|view)`;

  const pattern = isJbicModule
    ? `${instanceType}_\\d*-(images|events)-(read|view)`
    : `${instanceType}_\\d*(?:-(images|events))?-(read|view)`;

  const regexExpression = new RegExp(pattern, 'i');

  const regexExpressionForSubdivision = new RegExp(subdivisionPattern, 'i');
  for (const readModule of readModules) {
    if (
      regexExpression.test(readModule) ||
      regexExpressionForSubdivision.test(readModule)
    ) {
      return true;
    }
  }

  return false;
}

/**
 * @deprecated
 * This guard has been replaced by CasbinRouteGuard
 */
export const AdminRouteGuard: NavigationGuard = (to, from, next) => {
  const shouldCheck = to.meta && to.meta.adminOnly;
  if (!shouldCheck) {
    return next();
  }

  if (store.getters['rbac/isAdmin']) {
    return next();
  } else {
    return next({
      name: 'home'
    });
  }
};

/**
 * **Responsibility: Profile Completeness**
 *
 * It answers the question:
 * "Assuming the user is authenticated, is their profile complete enough to use
 * this part of the site?"
 *
 * This guard is now the single source of truth for all business logic related to
 * profile validation. It correctly checks for both missing attributes
 * (`getAttributeValueValidity`) and the `destinationAppUrl` flag, redirecting to
 * the user's profile if either check fails.
 */
export const UserAttributeRouteGuard: NavigationGuard = async (
  to,
  from,
  next
) => {
  if (to.meta && to.meta.isPublic) {
    return next();
  }

  if (
    to.meta &&
    to.meta.requiresConditionalAuth &&
    !groupRegistrationEnabled()
  ) {
    return next();
  }

  const shouldCheck: boolean = to.meta && to.meta.checkUserAttribute;
  if (!shouldCheck) {
    return next();
  }

  // Skip check if we are just changing query parameters on the same route
  // but ensure it runs on initial load/hard reload (when from.name is null)
  if (from && from.name && to.path === from.path) {
    return next();
  }

  try {
    // Check the attribute value validity
    // If any missing required attribute value found, redirect to profile.
    await store.dispatch('userAttribute/getAttributeValueValidity');
    const isMissingAttributeValue: boolean =
      store.getters['userAttribute/isMissingAttributeValue'];
    if (isMissingAttributeValue) {
      return next({ name: 'UserProfile' });
    }

    // Check if any destination app url stored for this user, if yes it means
    // user still have missing required attribute value, so redirect them to profile
    await store.dispatch('profile/getDestinationAppUrl');
    const destinationAppUrl: string =
      store.getters['profile/destinationAppUrl'];
    if (destinationAppUrl) {
      return next({ name: 'UserProfile' });
    }

    return next();
  } catch (error) {
    // This catch block handles unexpected errors during the checks.
    console.error(
      'An error occurred during UserAttributeRouteGuard checks:',
      error
    );
    redirectToLoginSignup();
    return next(false);
  }
};

/**
 * As per the name of the function, the intent of this function is to set
 * context of the application. As of the writing of this comment, the user could
 * be in one of two contexts - JAAS or JTMS. Depending on the context, what
 * navigation links they see change. JTMS, for example, has a completely
 * different set of links.
 * @param to
 * @param from
 * @param next
 * @constructor
 */
export const SetContext: NavigationGuard = async (to, from, next) => {
  if (to.path.includes('/admin/jtms')) {
    // Store context in Vuex
    await store.dispatch(
      'applicationContext/setApplicationContext',
      ApplicationContext.JTMS
    );
  } else {
    await store.dispatch(
      'applicationContext/setApplicationContext',
      ApplicationContext.JAAS
    );
  }

  return next();
};
